/**
 * Utilities for handling rating of the native app
 */

import versionCompare from 'node-version-compare';
import { APP } from '../config';
import nonCriticalException from './exceptionLogger';

const {
  IOS_VERSION,
  APP_VERSION,
  APP_STORE_ID,
  PLAY_STORE_ID,
} = window;

const STORAGE_KEY = 'const.rated_app';

/**
 * Determines whether the current app version is newer than the most recently rated app version.
 * @returns {Boolean} True if the current app version is newer and we are running in an app,
 * otherwise false.
 */
export const shouldShowRatingView = () => {
  const latestRatedVersion = localStorage.getItem(STORAGE_KEY);
  const currentVersion = APP_VERSION;

  if (!APP) {
    // if not running in app, don't try to show rating view
    return false;
  }

  return (
    // we don't have a latest version
    latestRatedVersion == null
    // latest version is "true" for some reason
    || latestRatedVersion === 'true'
    // latest rated version is older than current version, which is a surprisingly complicated
    // calculation
    || versionCompare(latestRatedVersion, currentVersion) < 0
  );
};

/**
 * Handles success signal sent by `LaunchReview`.
 */
const onRatingSuccess = () => {
  localStorage.setItem(STORAGE_KEY, APP_VERSION);
};

/**
 * Handles errors sent by `LaunchReview`.
 * @param {*} err The error message
 */
const onRatingError = err => {
  nonCriticalException(err);
};

/**
 * Opens the native rating dialog or app store, depending on the device.
 */
export const sendPositiveRating = () => {
  // this comes from the `cordova-launch-review` plugin
  const { LaunchReview } = window;

  if (APP && LaunchReview) {
    if (LaunchReview.isRatingSupported()) {
      // show rating dialog on iOS 10.3+
      LaunchReview.rating(onRatingSuccess, onRatingError);
    } else {
      // open app store for older iOS and Android
      LaunchReview.launch(
        onRatingSuccess,
        onRatingError,
        IOS_VERSION ? APP_STORE_ID : PLAY_STORE_ID,
      );
    }
  }
};

/**
 * Sends the user to our zendesk feedback URL.
 */
export const sendNegativeRating = () => {
  // use the `_system` target to open the link in the safari browser (rather than an in-app browser)
  window.open('https://styleseat.freshdesk.com/support/tickets/new', '_system');
};
